<script setup lang="ts">
import { type MedicationDoses } from '@/api/medications';
import { doseStatusColor, doseStatusText, recordPeople } from '@/utils/medication-doses';

// What happened with the patient's medications from the shift's start to its
// end: each dose due with its record, and the SOS and extra doses given.
// Read-only: doses are recorded and voided from the patient's Medicamentos.
defineProps<{ doses: MedicationDoses }>();
</script>
<template>
  <div class="d-flex flex-column ga-4 pa-2">
    <p
      v-if="doses.doses.length === 0"
      class="text-body-2 text-medium-emphasis"
    >
      Sin dosis con horario en este turno.
    </p>
    <v-list
      v-else
      lines="two"
      density="compact"
    >
      <v-list-item
        v-for="dose in doses.doses"
        :key="`${dose.dosageId}-${dose.scheduledAt}`"
      >
        <template #prepend>
          <span class="time font-weight-bold mr-4">{{ dose.scheduledTime }}</span>
        </template>
        <v-list-item-title>
          {{ dose.drugName }}
          <v-chip
            v-if="dose.highAlert"
            color="error"
            size="x-small"
            variant="tonal"
            class="ml-1"
          >
            Alto riesgo
          </v-chip>
        </v-list-item-title>
        <v-list-item-subtitle>
          {{ dose.summary }}
          <template v-if="dose.administration">
            · {{ recordPeople(dose.administration) }}
            <span v-if="dose.administration.reason">· {{ dose.administration.reason }}</span>
            <span v-if="dose.administration.notes">· {{ dose.administration.notes }}</span>
            <span v-if="dose.administration.offSchedule">· fuera de horario</span>
          </template>
        </v-list-item-subtitle>
        <template #append>
          <v-chip
            :color="doseStatusColor(dose)"
            size="small"
            variant="tonal"
          >
            {{ doseStatusText(dose) }}
          </v-chip>
        </template>
      </v-list-item>
    </v-list>

    <template v-if="doses.unscheduled.length > 0">
      <div class="text-subtitle-2 font-weight-bold">
        Dosis SOS y extra
      </div>
      <v-list
        lines="two"
        density="compact"
      >
        <v-list-item
          v-for="record in doses.unscheduled"
          :key="record.id"
        >
          <template #prepend>
            <span class="time font-weight-bold mr-4">{{ record.givenTime }}</span>
          </template>
          <v-list-item-title>
            {{ record.drugName }} · {{ record.extraDose ? 'Dosis extra' : 'SOS' }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ [recordPeople(record), record.reason, record.notes].filter(Boolean).join(' · ') }}
          </v-list-item-subtitle>
        </v-list-item>
      </v-list>
    </template>
  </div>
</template>
<style scoped>
.time {
  min-width: 48px;
}
</style>
